<template>
  <Card variant="tertiary" size="large" id="rp-showing-card">
    <View gap="sm">
      <Icon v-if="isShowingConfirmed" name="calendar-check-01" size="small" class="confirmed-icon" />

      <View direction="column" :gap="isShowingPending ? 'md' : 'sm'" :grow="1">
        <View justify="space-between" align="center" gap="sm">
          <Text variant="body-small" color="secondary">{{ title }}</Text>
          <Text variant="body-small" color="tertiary" :title="requestedAt">{{ timeAgo }}</Text>
        </View>

        <div>
          <Text
            id="rp-showing-datetime"
            :variant="isShowingPending ? 'h2' : 'h4'"
            v-if="showingStartTime && showingEndTime"
          >
            <Link :to="showingLink">{{ dateWithTime }}</Link>
            <span>&nbsp;</span>
            <Badge
              v-if="tourTypeBadge"
              size="small"
              :variant="tourTypeBadge.variant"
              :icon="tourTypeBadge.icon"
              :label="tourTypeBadge.label"
              class="tour-badge"
            />
          </Text>
        </div>

        <View direction="row" gap="sm" align="center" wrap="wrap">
          <View :grow="1" gap="sm" align="center">
            <Link :to="propertyLink" :decoration="false">
              <PropertyPhotoPreview :image-url="propertyPhoto" size="xs" />
            </Link>

            <Link :to="propertyLink">
              <Text variant="label-small">{{ propertyAddress }}</Text>
            </Link>
          </View>

          <Button v-if="isShowingConfirmed" :to="showingLink" size="small" variant="outline">Reconsider</Button>
        </View>

        <div class="actions" v-if="isShowingPending">
          <Button size="small" :data-manage-action="propertyId" @click="openAcceptModal">Accept</Button>
          <Button size="small" variant="outline" :data-manage-action="propertyId" @click="openRescheduleModal">
            Reschedule
          </Button>
          <Button
            size="small"
            variant="outline"
            appearance="primary"
            :data-manage-action="propertyId"
            @click="openDeclineModal"
          >
            Not interested
          </Button>
        </div>
      </View>
    </View>

    <AcceptShowingRequestModal
      ref="acceptModalRef"
      :renter-name="userName"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      :showing-agent-id="showingAgentId"
      :tour-type="tourType"
    />

    <DeclineShowingRequestModal
      ref="declineModalRef"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      :renter-name="userName"
      @reschedule="openRescheduleModal"
    />

    <RescheduleShowingRequestModal
      ref="rescheduleModalRef"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      :showing-agent-id="showingAgentId"
      :open-new-showing-after-reschedule="openNewShowingAfterReschedule"
      :current-start-time="showingStartTime"
      :tour-type="tourType"
    />
  </Card>
</template>

<script setup lang="ts">
import { useDateFormat, useTimeAgo } from '@vueuse/core';
import { computed, ref } from 'vue';

import { Button, Card, Icon, Link, Text, View, Badge } from '@tallo/design-system';
import { propertyDashboardRoute, showingDetailsRoute } from '@tallo/investor/navigation';
import { PropertyPhotoPreview } from '@tallo/property';
import { useTimeAgoShortMessages } from '@tallo/utility';

import { ShowingRequestStatus } from '../../interfaces/showing-request-status.enum';
import { ShowingRequestDto } from '../../interfaces/showing-request.interface';
import { TourType } from '../../interfaces/tour-type.enum';
import { getShowingTime } from '../../utils/get-showing-time.util';
import AcceptShowingRequestModal from './accept-showing-request-modal.vue';
import DeclineShowingRequestModal from './decline-showing-request-modal.vue';
import RescheduleShowingRequestModal from './reschedule-showing-request-modal.vue';

interface ShowingRequestActionsProps {
  propertyId: string;
  propertyAddress: string;
  propertyPhoto?: string;
  showingRequest: ShowingRequestDto;
  showingId: string;
  tourType: TourType;
  showingAgentId?: string;
  showingRequestStatus: ShowingRequestStatus;
  showingStartTime?: string | Date;
  showingEndTime?: string | Date;
  openNewShowingAfterReschedule?: boolean;
}

const props = defineProps<ShowingRequestActionsProps>();

const acceptModalRef = ref<typeof AcceptShowingRequestModal | null>(null);
const declineModalRef = ref<typeof DeclineShowingRequestModal | null>(null);
const rescheduleModalRef = ref<typeof RescheduleShowingRequestModal | null>(null);
const userName = computed(() => props.showingRequest.renter.user.name);
const dateWithTime = computed(() => {
  if (props.showingStartTime && props.showingEndTime) {
    return `${useDateFormat(props.showingStartTime, 'dddd').value}, ${getShowingTime(props.showingStartTime, props.showingEndTime)}`;
  }

  return '';
});
const requestedAt = computed(() => useDateFormat(props.showingRequest.createdAt, 'MM/DD/YYYY hh:mm').value);
const timeAgo = computed(() => useTimeAgo(props.showingRequest.createdAt, { messages: useTimeAgoShortMessages }).value);
const showingLink = computed(() => showingDetailsRoute(props.showingId));
const propertyLink = computed(() => propertyDashboardRoute(props.propertyId));
const isShowingConfirmed = computed(() => props.showingRequestStatus === ShowingRequestStatus.ACCEPTED);
const isShowingPending = computed(() => props.showingRequestStatus === ShowingRequestStatus.PENDING);
const title = computed(() => {
  switch (true) {
    case isShowingConfirmed.value:
      return `Scheduled for`;
    case isShowingPending.value:
      return `Showing request`;
    default:
      return '';
  }
});

const tourTypeBadge = computed(() => {
  switch (props.tourType) {
    case TourType.IN_PERSON:
      return { label: 'In-person tour', variant: 'blue', icon: 'user-01' } as const;
    case TourType.VIRTUAL:
      return { label: 'Virtual tour', variant: 'orange', icon: 'video-recorder' } as const;
    case TourType.SELF_GUIDED:
      return { label: 'Self-guided tour', variant: 'violet', icon: 'key' } as const;
    default:
      return null;
  }
});

function openAcceptModal() {
  acceptModalRef.value?.open();
}

function openDeclineModal() {
  declineModalRef.value?.open();
}

function openRescheduleModal() {
  rescheduleModalRef.value?.open();
}
</script>

<style scoped lang="scss">
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--t-spacing-xs);
  max-width: 23.75rem;
}

.tour-badge {
  display: inline-flex;
}

.confirmed-icon {
  color: var(--t-color-green-500);
}
</style>
